// src/utils/qrScannerUtils.ts
import QrScanner from "qr-scanner";

QrScanner.WORKER_PATH = "/qr-scanner-worker.min.js";


export async function scanImageFile(file: File, onProgress?: (msg: string) => void): Promise<string | null> {
  const log = (m: string) => { try { onProgress?.(m); console.debug(m); } catch {} };

  const tryQrScannerDirect = async (src: File | HTMLCanvasElement) => {
    try {
      const result = await QrScanner.scanImage(
        src,
        {
          returnDetailedScanResult: true,
          alsoTryWithoutScanRegion: true,
        }
      );
      if (!result) return null;
      if (typeof result === "string") return result;
      if ((result as any).data) return (result as any).data;
      if ((result as any).result) return (result as any).result;
      return null;
    } catch (err) {
      throw err;
    }
  };

  const createCanvasContext = (w: number, h: number) => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(w));
    canvas.height = Math.max(1, Math.floor(h));
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Could not create canvas 2D context");
    return { canvas, ctx };
  };

  const enhanceImageData = (ctx: CanvasRenderingContext2D, w: number, h: number, contrast = 1.3, brightness = -10) => {
    try {
      const imageData = ctx.getImageData(0, 0, w, h);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const avg = (d[i] + d[i + 1] + d[i + 2]) / 3;
        let v = (avg - 128) * contrast + 128 + brightness;
        v = Math.max(0, Math.min(255, v));
        d[i] = d[i + 1] = d[i + 2] = v;
      }
      ctx.putImageData(imageData, 0, 0);
    } catch (err) {
      console.warn("enhanceImageData failed:", err);
    }
  };

  try {
    log("Trying direct scan via qr-scanner...");
    const direct = await tryQrScannerDirect(file);
    if (direct) {
      log("Direct scan succeeded.");
      return direct;
    }
    log("Direct scan returned no result; proceeding to fallback preprocessing.");
  } catch (err) {
    log(`Direct scan threw: ${String(err)} - continuing to preprocessing fallback.`);
  }

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = (e) => reject(new Error("Failed to load image"));
    i.src = URL.createObjectURL(file);
  });

  const W = img.width;
  const H = img.height;
  log(`Image loaded: ${W} x ${H}`);

  const candidateFracs = [0.06, 0.09, 0.12, 0.18]; 
  const corners = [
    { x: 0, y: 0, label: "tl" }, // top-left
    { x: W, y: 0, right: true, label: "tr" }, // top-right
    { x: 0, y: H, bottom: true, label: "bl" }, // bottom-left
    { x: W, y: H, right: true, bottom: true, label: "br" }, // bottom-right
    { x: Math.floor(W / 2), y: Math.floor(H / 2), center: true, label: "center" } // center
  ];
  const rotations = [0, 90, 180, 270];
  const upscales = [3, 4, 6];

  // Iterate candidate windows
  for (const frac of candidateFracs) {
    const windowSize = Math.min(W, H) * frac;
    if (windowSize < 8) continue;

    for (const corner of corners) {
      let sx = corner.x;
      let sy = corner.y;

      if (corner.right && corner.bottom) {
        sx = Math.max(0, corner.x - windowSize);
        sy = Math.max(0, corner.y - windowSize);
      } else if (corner.right) {
        sx = Math.max(0, corner.x - windowSize);
        sy = Math.max(0, corner.y);
      } else if (corner.bottom) {
        sx = Math.max(0, corner.x);
        sy = Math.max(0, corner.y - windowSize);
      } else if ((corner as any).center) {
        sx = Math.max(0, corner.x - windowSize / 2);
        sy = Math.max(0, corner.y - windowSize / 2);
      } else {
        sx = Math.max(0, corner.x);
        sy = Math.max(0, corner.y);
      }

      const sw = Math.min(windowSize, W - sx);
      const sh = Math.min(windowSize, H - sy);
      if (sw < 8 || sh < 8) continue;

      for (const scale of upscales) {
        const { canvas, ctx } = createCanvasContext(sw * scale, sh * scale);

        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

        enhanceImageData(ctx, canvas.width, canvas.height, 1.4, -8);

        for (const rot of rotations) {
          let targetCanvas = canvas;

          if (rot !== 0) {
            const rc = document.createElement("canvas");
            if (rot === 90 || rot === 270) {
              rc.width = canvas.height;
              rc.height = canvas.width;
            } else {
              rc.width = canvas.width;
              rc.height = canvas.height;
            }
            const rctx = rc.getContext("2d", { willReadFrequently: true })!;
            rctx.translate(rc.width / 2, rc.height / 2);
            rctx.rotate((rot * Math.PI) / 180);
            rctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);
            targetCanvas = rc;
          }

          try {
            log(`Trying frac=${frac.toFixed(3)} corner=${(corner as any).label} scale=${scale} rot=${rot}`);
            const res = await tryQrScannerDirect(targetCanvas);
            if (res) {
              try { URL.revokeObjectURL(img.src); } catch {}
              log("Decoded with fallback preprocessing!");
              return res;
            }
          } catch (err) {
            // Continue on errors
            log(`Attempt decode failed: ${String(err)}`);
          }
        } // rotations
      } // upscales
    } // corners
  } // candidateFracs

  try { URL.revokeObjectURL(img.src); } catch {}
  log("No QR code found after all strategies");
  return null;
}
